import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Keep the pending write separate from confirmation so dismissal never writes. */
export function useProjectCompletionConfirmation() {
  const pendingAction = useRef(null);
  const submitting = useRef(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestCompletion = (action) => {
    if (pendingAction.current || submitting.current) return;
    pendingAction.current = action;
    setOpen(true);
  };

  const cancel = () => {
    if (submitting.current) return;
    pendingAction.current = null;
    setOpen(false);
  };

  const complete = async () => {
    if (!pendingAction.current || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      await pendingAction.current();
    } finally {
      pendingAction.current = null;
      submitting.current = false;
      setBusy(false);
      setOpen(false);
    }
  };

  const completionDialog = (
    <AlertDialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) cancel(); }}>
      <AlertDialogContent data-testid="project-completion-confirmation">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to complete this project?</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            Select Complete to record project completion, or Cancel to keep the project unchanged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} onClick={cancel}>Cancel</AlertDialogCancel>
          <Button disabled={busy} onClick={complete} type="button">
            {busy ? "Completing…" : "Complete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { requestCompletion, completionDialog };
}
